import { Injectable, EventEmitter } from '@angular/core';
import { Subscription, BehaviorSubject, Subject, Observable, combineLatest } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';

import { Wall } from '../../../shared/models/wall.type';
import { WallMonitor } from '../../../shared/models/monitor.type';
import { TransferInfo } from '../../../shared/models/transfer-info.type';
import { WallChangeEventEnum } from '../../../shared/enums/wall-change-event.enum';
import { WallIndexEnum } from '../../../shared/enums/wall-index.enum';
import { MonitorAdjustment } from '../models/monitor-adjustment.model';
import { FunctionType } from '../../../shared/enums/function-type.enum';
import { IApplyWallData } from '../../../../wall-module/shared/interfaces/apply-wall-data.interface';
import { AppConfig } from '../../../../app.settings';
import { SharedPresetService } from '../../../../wall-module/shared/services/shared-preset.service';
import { Preset } from '../../../shared/models/preset.type';
import { MonitorHub } from '../../../../shared-module/hubs/monitor.hub';
import { WallSharedConstants } from '../../../wall-shared.constants';
import { SpaceWallManagementService } from './space-wall-management.service';
import { WallCalibrationModel } from '../../../shared/models/wall-calibration.type';
import { AuthService } from '../../../../core/services/auth/auth.service';
import { WallManagementService } from '../../../shared/services/wall-management.service';
import { CurrentWallApp } from '../../../shared/enums/current-wall-app.enum';

@Injectable()
export class SpaceWallPresetsService extends SharedPresetService {
  private subscriptions: Subscription = new Subscription();

  _preset: Preset;
  viewportTransform: BehaviorSubject<number[]> = new BehaviorSubject(null);
  presetSubject: BehaviorSubject<Preset> = new BehaviorSubject<Preset>(null);

  extendedGroupSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  mirrorGroupsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  wallGroupsSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  recountWall: EventEmitter<boolean> = new EventEmitter();
  updateLockedCoordinates: Subject<void> = new Subject();
  updateIsAppliedWall: BehaviorSubject<IApplyWallData[]> = new BehaviorSubject([]);
  isMonitorSettingsChanged: Subject<boolean> = new Subject();
  isAppliedButtonHighlighted: BehaviorSubject<boolean> = new BehaviorSubject(false);
  isWallBeforeApplied: Subject<void> = new Subject();
  wallBeforeCalibration: BehaviorSubject<Wall[]> = new BehaviorSubject(null);
  private isPresetChanged: boolean;

  constructor(
    public readonly httpClient: HttpClient,
    public readonly config: AppConfig,
    public readonly authService: AuthService,
    private readonly monitorHub: MonitorHub,
    private readonly spaceWallManagementService: SpaceWallManagementService,
    private readonly wallManagementService: WallManagementService
  ) {
    super(httpClient, config, authService);

    this.monitorHub.WallsPresetUpdated.subscribe((presetToDraw: Preset) => {
      if (presetToDraw) {
        this.init(presetToDraw);
        if (presetToDraw.Walls) {
          this.shouldChangeActiveWallTab.next(presetToDraw.Walls.length);
        }

        this.isAppliedButtonHighlighted.next(!!presetToDraw.Title);

        if (!presetToDraw.Title) {
          this.isPresetApplied = true;
        }
        else {
          this.isPresetApplied = false;
          this.isPresetChanged = false;
        }
      }
    });

    this.subscriptions.add(
      this.isAppliedButtonHighlighted.subscribe((isHighlighted) => {
        this.isPresetChanged = isHighlighted;
      })
    );

    this.subscriptions.add(
      this.monitorHub.WallSuccessfullyApply.subscribe(() => {
        this.requestRefreshPreset();
      })
    );

    this.subscriptions.add(
      this.monitorHub.WallCleaning.subscribe(() => {
        this.clearAllWalls();
      })
    );

    this.applyCreatedPreset();
  }

  requestRefreshPreset(): void {
    const isSpaceWallRunning: boolean = this.wallManagementService.currentAppName.value === CurrentWallApp.SpaceWall;
    if (isSpaceWallRunning) {
      this.subscriptions.add(this.refreshPresetCall(this._preset.MachinePhysicalAddress).subscribe());

      const calibratedWallIds: number[] = _.map(this._preset.Walls, (wall: Wall) => {
        if (wall && wall.IsCalibrated) {
          return wall.Id;
        }
        return null;
      });
      this.calibratedWallIds = calibratedWallIds;
    }
  }

  addMirror(wall: Wall = null) {
    if (
      this._preset.MirrorGroups[this._preset.MirrorGroups.length - 1].Monitors
        .length > 0
    ) {
      this._preset.MirrorGroups.push(
        wall ? wall : new Wall([], this._preset.MirrorGroups.length, { GroupId: this._preset.Walls.length })
      );
    }
    this.mirrorGroupsSubject.next({
      content: this._preset.MirrorGroups,
      event: WallChangeEventEnum.WallAdded
    });
  }

  addWall(wall: Wall = null) {
    if (
      this._preset.Walls[this._preset.Walls.length - 1].Monitors
        .length > 0
    ) {
      if (!wall) {
        wall = new Wall([], this._preset.Walls.length, { GroupId: this._preset.Walls.length });
        wall.Height = 0;
        wall.Width = 0;
      }
      this._preset.Walls.push(wall);
    }

    this.wallGroupsSubject.next({
      content: this._preset.Walls,
      event: WallChangeEventEnum.WallAdded
    });
  }

  removeMirror(group: Wall) {
    const ids: number[] = _.map(
      group.Monitors,
      WallSharedConstants.MONITOR_IDENTIFIER
    ); // monitor ids
    const index = this._preset.MirrorGroups.findIndex(x => x.Id === group.Id);

    ids.forEach(k => {
      const monitor = this._preset.MirrorGroups[index].Monitors.find(
        x => x[WallSharedConstants.MONITOR_IDENTIFIER] === k
      );
      const data = new TransferInfo(
        monitor,
        FunctionType.mDisplay,
        monitor.GroupId,
        FunctionType.iDisplay,
        this._preset.ExtendedGroup.Id
      );
      this.moveMonitor(data);
    });

    if (this._preset.MirrorGroups.length > 1) {
      // delete mirror group (now empty)
      if (index > -1) {
        this._preset.MirrorGroups.splice(index, 1);
      }

      // rewrite group ids
      for (let i = 0; i < this._preset.MirrorGroups.length; i++) {
        this._preset.MirrorGroups[i].Id = i;
        for (
          let j = 0;
          j < this._preset.MirrorGroups[i].Monitors.length;
          j++
        ) {
          this._preset.MirrorGroups[i].Monitors[j].GroupId = i;
        }
      }
    }

    this.extendedGroupSubject.next({
      content: this._preset.ExtendedGroup,
      event: WallChangeEventEnum.WallRefreshed
    });

    this.mirrorGroupsSubject.next({
      content: this._preset.MirrorGroups,
      event: WallChangeEventEnum.WallDeleted
    });
  }

  removeWall(group: Wall) {
    const ids: number[] = _.map(
      group.Monitors,
      WallSharedConstants.MONITOR_IDENTIFIER
    ); // monitor ids
    const index = this._preset.Walls.findIndex(x => x.Id === group.Id);

    ids.forEach(k => {
      // find and prepare to move
      const monitor = this._preset.Walls[index].Monitors.find(
        x => x[WallSharedConstants.MONITOR_IDENTIFIER] === k
      );
      const data = new TransferInfo(
        monitor,
        FunctionType.wDisplay,
        group.Id,
        FunctionType.iDisplay,
        this._preset.ExtendedGroup.Id
      );
      this.moveMonitor(data);
    });

    if (index > -1) {
      this._preset.Walls.splice(index, 1);
    }

    for (let i = 0; i < this._preset.Walls.length; i++) {
      this._preset.Walls[i].GroupId = i;
      for (let j = 0; j < this._preset.Walls[i].Monitors.length; j++) {
        this._preset.Walls[i].Monitors[j].GroupId = i;
      }
    }

    if (this._preset.Walls.length === 0) {
      this._preset.Walls.push(new Wall([], 0));
    }

    this.extendedGroupSubject.next({
      content: this._preset.ExtendedGroup,
      event: WallChangeEventEnum.WallRefreshed
    });

    this.wallGroupsSubject.next({
      content: this._preset.Walls,
      event: WallChangeEventEnum.WallDeleted
    });
  }

  onDnD(event: TransferInfo) {
    if (event.sourceType.valueOf() === FunctionType.wDisplay) {
      const wall = this._preset.Walls[event.sourceId];
      if (wall && wall.IsCalibrated) {
        this.updateCalibrationScreens(this._preset.MachinePhysicalAddress,
          wall);
        wall.IsCalibrated = false;
      }
    }

    this.moveMonitor(event);

    if (
      event.sourceType.valueOf() === FunctionType.iDisplay ||
      event.destinationType.valueOf() === FunctionType.iDisplay
    ) {
      const extendedGroupContent: Wall = _.cloneDeep(this._preset.ExtendedGroup);
      extendedGroupContent.Monitors = extendedGroupContent.Monitors.map((monitor: WallMonitor) => new WallMonitor(monitor));
      this.extendedGroupSubject.next({
        content: extendedGroupContent,
        event: WallChangeEventEnum.WallRefreshed
      });
    }

    if (
      event.sourceType.valueOf() === FunctionType.mDisplay ||
      event.destinationType.valueOf() === FunctionType.mDisplay
    ) {
      this.mirrorGroupsSubject.next({
        content: this._preset.MirrorGroups,
        event: WallChangeEventEnum.WallRefreshed
      });
    }

    if (
      event.sourceType.valueOf() === FunctionType.wDisplay ||
      event.destinationType.valueOf() === FunctionType.wDisplay
    ) {
      this.wallGroupsSubject.next({
        content: this._preset.Walls,
        event: WallChangeEventEnum.WallRefreshed
      });
    }
  }

  preparePresetToCreate(currentPreset?: Preset): Observable<Preset> {
    this.recountWall.emit(true);
    const preset = this.getUpdatedPreset(true);
    preset.Title = currentPreset.Title;
    preset.isActive = this.isPresetApplied;
    return this.createNewPreset(preset);
  }

  // calls to 'backend'
  apply() {
    this.isWallBeforeApplied.next();
    this.wallBeforeCalibration.next(null);

    this.recountWall.emit(true);
    this.updateLockedCoordinates.next();
    setTimeout(() => {
      const preset = this.getUpdatedPreset(true);
      this.checkIfPresetHasChanged(preset);
      if (preset && preset.Walls && preset.Walls.length) {
        this.updateIsAppliedWall.next(preset.Walls.map((currentWall: Wall) => {
          return { WallId: currentWall.Id, isApplied: currentWall.Monitors && !!currentWall.Monitors.length };
        }));
      }

      this.monitorHub.applyIsInPending.next(true);
      this.subscriptions.add(
        this.applyPreset(this._preset.MachinePhysicalAddress, preset).subscribe(
          () => this.wallManagementService.turnOffScreensIdentification(this._preset.MachinePhysicalAddress).subscribe(),
          () => this.monitorHub.applyIsInPending.next(false)
        )
      );
    });
  }

  cancel() {
    this.requestRefreshPreset();
  }

  reset() {
    this.updateIsAppliedWall.next([]);
    this.monitorHub.resetIsInPending.next(true);
    this.wallManagementService.turnOffScreensIdentification(this._preset.MachinePhysicalAddress).subscribe();

    this.subscriptions.add(
      this.wallManagementService.resetWall(this._preset.MachinePhysicalAddress).subscribe(
        () => {},
        () => this.monitorHub.resetIsInPending.next(false)
      )
    );
  }

  updateCalibrationScreens(physicalAddress: string, wall: Wall) {
    const wallCalibrationModel: WallCalibrationModel = this.convertToWallCalibrationModel(wall);
    this.subscriptions.add(
      this.spaceWallManagementService.calibrateWall(physicalAddress, wallCalibrationModel).subscribe()
    );
  }

  applyMonitorAdjustments(physicalAddress: string, monitorAdjustment: MonitorAdjustment) {
    this.subscriptions.add(
      this.spaceWallManagementService.adjustMonitor(physicalAddress, monitorAdjustment).subscribe()
    );
  }

  setWallIndex(walls: Wall[], type: WallIndexEnum): Wall[] {
    const existedIds = walls.map(w => w.WallIndex);

    walls.forEach((group, index) => {
      if (
        'WallIndex' in group &&
        (group.WallIndex !== undefined || group.WallIndex !== null)
      ) {
        return;
      }

      const wallIndex = this.getFirstFreeIndex(type, existedIds);

      group.WallIndex = wallIndex;
      group.Monitors.forEach(monitor => {
        monitor.WallIndex = wallIndex;
      });

      existedIds.push(wallIndex);
    });

    return walls;
  }

  wallMonitorDeleted(event: WallMonitor) {
    const data = new TransferInfo(
      event,
      FunctionType.wDisplay,
      event.GroupId,
      FunctionType.iDisplay,
      this._preset.ExtendedGroup.Id
    );

    this.moveMonitor(data);

    this.wallGroupsSubject.next({
      content: this._preset.Walls,
      event: WallChangeEventEnum.WallRefreshed
    });

    this.extendedGroupSubject.next({
      content: this._preset.ExtendedGroup,
      event: WallChangeEventEnum.WallRefreshed
    });
  }

  detectChangesInPreset(): void {
    this.subscriptions.add(
      this.monitorHub.WallsPresetUpdated
      .pipe(
        mergeMap(() => {
          this.checkGroupsSubject();
          return this.isMonitorSettingsChanged;
        })
      )
      .subscribe((isMonitorSettingsChanged) => {
        this.isAppliedButtonHighlighted.next(isMonitorSettingsChanged);
        this.isPresetApplied = !isMonitorSettingsChanged;
      })
    );
  }

  private applyCreatedPreset(): void {
    this.subscriptions.add(
      this.createdPresetToApply
      .pipe(
        mergeMap((preset) => {
          const presetToDraw = this.createPresetFromResponse(JSON.stringify(preset), true);
          preset = Preset.Restore(presetToDraw);
          const presetToApply = this.getUpdatedPreset(true, preset);
          presetToApply.isUpdated = false;
          return this.applyPreset(this._preset.MachinePhysicalAddress, presetToApply);
        })
      )
      .subscribe()
    );
  }

  private init(preset: Preset) {
    const presetToDraw = this.createPresetFromResponse(JSON.stringify(preset), true);
    preset = Preset.Restore(presetToDraw);
    this.refreshPreset(preset);
    this.wallGroupsSubject.next({
      content: preset.Walls,
      event: WallChangeEventEnum.WallInit
    });
    if (this.calibratedWallIds && this.calibratedWallIds.length) {
      this._preset.Walls.forEach((wall: Wall) => {
        this.calibratedWallIds.forEach((wallId: number) => {
          if (wallId !== null && wallId === wall.Id) {
            wall.IsCalibrated = true;
          }
        });
      });
    }
    this.monitorHub.isWallRefreshing.next(false);
    this.monitorHub.applyIsInPending.next(false);
    this.monitorHub.resetIsInPending.next(false);
    this.monitorHub.refreshIsInPending.next(false);
  }

  private checkGroupsSubject(): void {
    combineLatest([
      this.mirrorGroupsSubject,
      this.wallGroupsSubject,
    ])
    .subscribe(([mirrorGroup, wallGroup ]) => {
      if (
        mirrorGroup?.event &&
        mirrorGroup.event !== WallChangeEventEnum.WallInit ||
        wallGroup?.event &&
        wallGroup.event !== WallChangeEventEnum.WallInit
      ) {
        this.isAppliedButtonHighlighted.next(true);
        this.isPresetApplied = false;
      }
    });
  }

  private checkIfPresetHasChanged(preset: Preset): void {
    if (this.isPresetChanged) {
      preset.Id = null;
    }
  }

  private createPresetFromResponse(value: string, discardCalibration: boolean = false): Preset {
    const response = JSON.parse(value);
    const preset = new Preset({
      Id: response.Id,
      Title: response.Title,
      Quality: response.Quality
    });

    preset.ExtendedGroup = new Wall(
      response.ExtendedGroup.Monitors.map((monitor: WallMonitor) => {
        if (monitor) {
          return new WallMonitor({ ...monitor, GroupId: response.ExtendedGroup.Id });
        }
      }),
      response.ExtendedGroup.Id,
      {
        GroupId: response.ExtendedGroup.Id,
        ...response.ExtendedGroup
      }
    );

    preset.MirrorGroups = response.MirrorGroups.length
      ? response.MirrorGroups.map((wall: Wall, index: number) => this.createNewWallInstance(wall, index))
      : [new Wall([], 0)];

    preset.Walls = response.Walls.length
      ? response.Walls.map((wall: Wall, index: number) => this.createNewWallInstance(wall, index))
      : [new Wall([], 0)];

    // TODO: need to investigate: repro when apply calibrated wall -> wal becomes unCalibrated
    if (discardCalibration) {
      preset.Walls.forEach(wall => wall.IsCalibrated = false);
    }

    return preset;
  }

  private createNewWallInstance(wall: Wall, groupId: number): Wall {
    return new Wall(
      wall.Monitors.map((monitor: WallMonitor) => {
        if (monitor) {
          const ArtisticOwnSize = !wall.IsAligned ? {
            Width: monitor.Width,
            Height: monitor.Height
          } : null;

          return new WallMonitor({
            ...monitor,
            GroupId: wall.Id,
            Width: wall.IsAligned ? WallSharedConstants.DEFAULT_WALL_RESOLUTION_W : monitor.Width,
            Height: wall.IsAligned ? WallSharedConstants.DEFAULT_WALL_RESOLUTION_H : monitor.Height,
            ArtisticOwnSize
          });
        }
      }),
      wall.Id,
      {
        ...wall,
        IsInit: true,
        GroupId: wall.GroupId || groupId
      }
    );
  }

  private refreshPreset(preset: Preset) {
    this._preset = preset;
    this._preset.MachinePhysicalAddress = this.selectedMachinePhysicalAddress.value;
    this.presetSubject.next(preset);

    this.extendedGroupSubject.next({
      content: preset.ExtendedGroup,
      event: WallChangeEventEnum.WallInit
    });
    this.mirrorGroupsSubject.next({
      content: preset.MirrorGroups,
      event: WallChangeEventEnum.WallInit
    });
    this.wallGroupsSubject.next({
      content: preset.Walls,
      event: WallChangeEventEnum.WallInit
    });
  }

  private clearAllWalls(): void {
    this.extendedGroupSubject.next(null);
    this.mirrorGroupsSubject.next(null);
    this.wallGroupsSubject.next(null);
  }

  private moveMonitor(data: TransferInfo) {
    const monitor = data.monitor;
    const sourceType = data.sourceType;
    const sourceId = data.sourceId;

    if (sourceType === FunctionType.iDisplay) {
      // delete from extended
      _.remove(
        this._preset.ExtendedGroup.Monitors,
        x =>
          x[WallSharedConstants.MONITOR_IDENTIFIER] ===
          monitor[WallSharedConstants.MONITOR_IDENTIFIER]
      );
    }

    if (sourceType === FunctionType.mDisplay) {
      // delete from mirror
      const indexMirror = this._preset.MirrorGroups.findIndex(
        (x: Wall) => x.Id === sourceId
      );
      _.remove(
        this._preset.MirrorGroups[indexMirror].Monitors,
        x =>
          x[WallSharedConstants.MONITOR_IDENTIFIER] ===
          monitor[WallSharedConstants.MONITOR_IDENTIFIER]
      );
    }
    if (sourceType === FunctionType.wDisplay) {
      // delete from mirror
      const indexWall = this._preset.Walls.findIndex(
        (x: Wall) => x.Id === sourceId
      );
      _.remove(
        this._preset.Walls[indexWall].Monitors,
        x =>
          x[WallSharedConstants.MONITOR_IDENTIFIER] ===
          monitor[WallSharedConstants.MONITOR_IDENTIFIER]
      );
    }

    if (data.destinationType.valueOf() === FunctionType.iDisplay) {
      monitor.Row = null;
      monitor.Column = null;
      monitor.Height = WallSharedConstants.DEFAULT_WALL_RESOLUTION_H;
      monitor.Width = WallSharedConstants.DEFAULT_WALL_RESOLUTION_W;
      monitor.LeftPosition = WallSharedConstants.DEFAULT_MONITOR_LEFT;
      monitor.TopPosition = WallSharedConstants.DEFAULT_MONITOR_TOP;
      monitor.Angle = WallSharedConstants.DEFAULT_MONITOR_ANGLE;
      monitor.IsRotated = false;

      monitor.FunctionType = FunctionType.iDisplay;
      monitor.GroupId = this._preset.ExtendedGroup.Id;
      delete monitor.WallIndex;

      this._preset.ExtendedGroup.Monitors.push(monitor);
    }

    if (data.destinationType.valueOf() === FunctionType.mDisplay) {
      monitor.Row = null;
      monitor.Column = null;
      monitor.Height = WallSharedConstants.DEFAULT_WALL_RESOLUTION_H;
      monitor.Width = WallSharedConstants.DEFAULT_WALL_RESOLUTION_W;
      monitor.LeftPosition = 0;
      monitor.TopPosition = 0;
      monitor.Angle = WallSharedConstants.DEFAULT_MONITOR_ANGLE;
      monitor.IsRotated = false;

      const target: Wall = this._preset.MirrorGroups.find(
        (x: Wall) => x.Id === data.destinationId
      );
      const index = this._preset.MirrorGroups.indexOf(target);

      monitor.FunctionType = FunctionType.mDisplay;
      monitor.GroupId = target.GroupId;
      monitor.WallIndex = this._preset.MirrorGroups[index].WallIndex;

      this._preset.MirrorGroups[index].Monitors.push(monitor);
    }

    if (data.destinationType.valueOf() === FunctionType.wDisplay) {
      const target = this._preset.Walls.find(
        (x: Wall) => x.Id === data.destinationId
      );
      const index = this._preset.Walls.indexOf(target);

      monitor.FunctionType = FunctionType.wDisplay;
      monitor.GroupId = target.GroupId;
      monitor.WallIndex = this._preset.Walls[index].WallIndex;

      this._preset.Walls[index].Monitors.push(monitor);
    }
  }

  private clearPreset(presetToUpdate?: Preset): Preset {
    const clean = presetToUpdate ?
      Preset.Duplicate(presetToUpdate) :
      Preset.Duplicate(this._preset);

    const mirrors = presetToUpdate ?
      this.updateIds(presetToUpdate.MirrorGroups) :
      this.updateIds(this._preset.MirrorGroups);

    const walls = presetToUpdate ?
      this.updateIds(presetToUpdate.Walls) :
      this.updateIds(this._preset.Walls);

    clean.MirrorGroups = mirrors;
    clean.Walls = walls;
    clean.ExtendedGroup = this.extendedGroupSubject.value.content;

    return clean;
  }

  private updateIds(walls: Wall[]): Wall[] {
    return walls
      .map((wall, idx) => ({
        ...wall,
        Id: idx,
        Monitors: wall.Monitors.map(monitor => ({ ...monitor, GroupId: idx })) // changing id to actual in relate wall
      }));
  }

  private getUpdatedPreset(isToCheckWallMonitorsLength: boolean = false, presetToUpdate?: Preset): Preset {
    const cleared = this.clearPreset(presetToUpdate);
    const preset = this.recountWallDimensions(cleared);

    if (isToCheckWallMonitorsLength && preset.Walls && preset.Walls.length > 1) {
      const modifyVideoWalls: Wall[] = preset.Walls.filter((wall: Wall) => {
        return !(wall && wall.Monitors && !wall.Monitors.length);
      });

      if (modifyVideoWalls.length !== preset.Walls.length) {
        this.shouldChangeActiveWallTab.next(modifyVideoWalls.length);
      }
      preset.Walls = modifyVideoWalls;
    }

    preset.Walls = preset.Walls.map((wall) => ({
      ...wall,
      IsCalibrated: false,
    }));

    return preset;
  }

  private getFirstFreeIndex(type: WallIndexEnum, existedIds: number[]) {
    const ids = this.getAllPossibleIndexes(type);

    return ids.filter(id => !existedIds.includes(id))[0];
  }

  private getAllPossibleIndexes(type: WallIndexEnum, quantityWallTypes = 2): number[] {
    const ids = Array(WallSharedConstants.MAX_WALLS_QUANTITY * quantityWallTypes)
      .fill('')
      .map((item, idx) => idx);

    let allPossibleIndexes: number[] = [];

    switch (type) {
      case WallIndexEnum.MirrorGroup:
        allPossibleIndexes = ids.slice(0, WallSharedConstants.MAX_WALLS_QUANTITY);
        break;

      case WallIndexEnum.VideoWallGroup:
        allPossibleIndexes = ids.slice(WallSharedConstants.MAX_WALLS_QUANTITY);
    }

    return allPossibleIndexes;
  }
}
